import { ActionButton } from '@ax2/common/components/ActionButton';
import ImgLogo from '@ax2/common/components/logo/ImgLogo';
import PageContent from '@ax2/common/components/page/PageContent';
import PageFooter from '@ax2/common/components/page/PageFooter';
import PageHeader, {
  PageHeaderProps,
} from '@ax2/common/components/page/PageHeader';
import TopBar from '@ax2/common/components/TopBar';
import {
  useErrorContext,
  useErrorUpdateContext,
} from '@ax2/common/contexts/ErrorContext';
import ErrorModal from 'components/ErrorModal';
import { usePanelRecommendationUpdateContext } from 'contexts/PanelRecommendationContext';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { animateScroll, Events, scroller } from 'react-scroll/modules';
import logoImg from '../assets/ax2_logo.svg';
import { useUserContext, useUserUpdateContext } from '../contexts/UserContext';
import { anchorListener, pathListener } from '../helpers/router';
import { signOut } from '../rest/authClient';
import base from '../styles/Base.module.css';
import MenuGrid from 'components/MenuGrid';
import useMediaQuery from '@ax2/common/helpers/hooks/useMediaQuery';
import borders from '@ax2/common/styles/Breakpoints.module.css';

export interface DefaultPageProps {
  header?: PageHeaderProps;
  wideContent?: boolean;
  noPaddingBottom?: boolean;
  paddingTop?: boolean;
}

const DefaultPage: React.FC<DefaultPageProps & RouteComponentProps> = ({
  header,
  wideContent = false,
  noPaddingBottom = false,
  paddingTop = false,
  children,
  history,
}) => {
  const user = useUserContext();
  const updateUser = useUserUpdateContext();
  const error = useErrorContext();
  const updateError = useErrorUpdateContext();
  const updatePanelRecommendations = usePanelRecommendationUpdateContext();
  const cleanPanelRecomendations = () => updatePanelRecommendations(null);
  const [submenuVisible, setSubmenuVisible] = useState(false);
  const up1024 = useMediaQuery(borders.lg);

  useEffect(() => {
    Events.scrollEvent.register('end', to => {
      // Ignore anchors starting with underscore, treat those as "private"
      if (to && history.location.hash !== '#' + to && !_.startsWith(to, '_')) {
        history.push(
          history.location.pathname + history.location.search + '#' + to
        );
      }
    });

    const unregisterAnchorListener = anchorListener(name => {
      if (!_.isEmpty(name)) {
        const element = scroller.get(name);
        if (element) {
          scroller.scrollTo(name, {
            duration: 400,
            delay: 0,
            offset: -20,
            smooth: 'easeInOutQuart',
          });
        }
      }
    });

    // Navigation is done programmatically, so in case when user is on some anchor,
    // and then presses some navigation button, they will remain scrolled down.
    // To fix that, we need to scroll back up again.
    const unregisterPathListener = pathListener(() => {
      animateScroll.scrollToTop({
        delay: 0,
        duration: 0,
      });
    });

    return () => {
      unregisterAnchorListener();
      unregisterPathListener();
      Events.scrollEvent.remove('end');
    };
  }, [history]);

  const toggleSubmenu = () => {
    setSubmenuVisible(prev => !prev);
  };

  return (
    <div id="page" className={base.pageContainer}>
      {error && (
        <ErrorModal
          errorText={error.text}
          actions={error.actions}
          onClose={() => {
            updateError(null);
            error.onClose && error.onClose();
          }}
        />
      )}
      <div id="header" className={base.headerContainer}>
        <TopBar
          logo={
            <ImgLogo
              source={logoImg}
              alt="EVERSANA"
              onClick={() => {
                history.push('/');
              }}
            />
          }
          leftItems={[
            <ActionButton
              key="1"
              text="Shop Panel Surveys"
              type="link"
              onClick={() => {
                cleanPanelRecomendations();
                history.push('/shop');
              }}
              shadowNormal="none"
              shadowHover="1"
            />,
            <ActionButton
              key="2"
              text="Support"
              type="link"
              onClick={() => history.push('/support')}
              shadowNormal="none"
              shadowHover="1"
            />,
          ]}
          rightItems={
            user && user.isAdmin
              ? up1024
                ? [
                    <MenuGrid
                      key="menu-grid"
                      onClick={toggleSubmenu}
                      submenuVisible={submenuVisible}
                    />,
                    <ActionButton
                      key="0"
                      text={user.firstName + "'s Dashboard"}
                      type="secondaryAlt"
                      onClick={() => history.push('/dashboard')}
                    />,
                    <ActionButton
                      key="1"
                      text="Logout"
                      type="link"
                      onClick={() => {
                        updateUser(null);
                        signOut().then(() => history.push('/signIn'));
                      }}
                      shadowNormal="1"
                      shadowHover="1"
                    />,
                  ]
                : [
                    <ActionButton
                      key="user-management"
                      text={'User Management'}
                      type="secondaryAlt"
                      onClick={() => history.push('/user-management?page=1')}
                    />,
                    <ActionButton
                      key="statistics"
                      text={'Survey Stats'}
                      type="secondaryAlt"
                      onClick={() => history.push('/metrics?page=1')}
                    />,
                    <ActionButton
                      key="0"
                      text={user.firstName + "'s Dashboard"}
                      type="secondaryAlt"
                      onClick={() => history.push('/dashboard')}
                    />,
                    <ActionButton
                      key="1"
                      text="Logout"
                      type="link"
                      onClick={() => {
                        updateUser(null);
                        signOut().then(() => history.push('/signIn'));
                      }}
                      shadowNormal="1"
                      shadowHover="1"
                    />,
                  ]
              : user
              ? [
                  <ActionButton
                    key="0"
                    text={
                      !user.isAdmin
                        ? user.firstName + "'s Dashboard"
                        : 'Dashboard'
                    }
                    type="secondaryAlt"
                    onClick={() => history.push('/dashboard')}
                  />,
                  <ActionButton
                    key="1"
                    text="Logout"
                    type="link"
                    onClick={() => {
                      updateUser(null);
                      signOut().then(() => history.push('/signIn'));
                    }}
                    shadowNormal="1"
                    shadowHover="1"
                  />,
                ]
              : [
                  <ActionButton
                    key="0"
                    text="Request a Demo"
                    type="primary"
                    onClick={() => history.push('/requestDemo')}
                  />,
                  <ActionButton
                    key="1"
                    text="Log-in"
                    type="link"
                    onClick={() => {
                      history.push('/signIn');
                    }}
                    shadowNormal="1"
                    shadowHover="1"
                  />,
                ]
          }
        />
        {header ? <PageHeader {...header} /> : null}
      </div>
      <div
        id="content"
        className={
          wideContent ? base.wideContentContainer : base.contentContainer
        }
      >
        <PageContent noPaddingBottom={noPaddingBottom} paddingTop={paddingTop}>
          {children}
        </PageContent>
      </div>
      <div id="footer" className={base.footerContainer}>
        <PageFooter
          withTerms
          onTerms={() => history.push('/termsAndConditions')}
        />
      </div>
    </div>
  );
};

export default withRouter(DefaultPage);
